import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { CookbookTransformation } from '../../types';
import { messages } from '../../i18n';

export interface TextOption {
  text: string;
}

export interface ComplexityOption {
  complexity: CookbookTransformation['complexity'];
}

export interface CategoryOption {
  category: string;
}

export interface IsInPresetsOption {
  isInPresets: boolean;
}

export interface IsAllowedForStrictTransformationsOption {
  isAllowedForStrictTransformations: boolean;
}

export type SearchOption = ComplexityOption | TextOption | CategoryOption | IsInPresetsOption | IsAllowedForStrictTransformationsOption;
export type SearchQuery = SearchOption[];

export const isPlaintextOption = (option: SearchOption): option is TextOption => 'text' in option;
export const isComplexityOption = (option: SearchOption): option is ComplexityOption => 'complexity' in option;
export const isCategoryOption = (option: SearchOption): option is CategoryOption => 'category' in option;
export const isIsInPresetsOption = (option: SearchOption): option is IsInPresetsOption => 'isInPresets' in option;
export const isIsAllowedForStrictTransformationsOption = (option: SearchOption): option is IsAllowedForStrictTransformationsOption =>
  'isAllowedForStrictTransformations' in option;

export const getIsInPresetsFromQuery = (query: SearchQuery) => {
  const allOptionsOfType = query.filter(isIsInPresetsOption);
  const trueOptionFound = allOptionsOfType.some((option) => option.isInPresets);
  const falseOptionFound = allOptionsOfType.some((option) => !option.isInPresets);

  if (trueOptionFound === falseOptionFound) {
    return null;
  }
  return allOptionsOfType[0].isInPresets;
};

export const getIsAllowedForStrictTransformationsFromQuery = (query: SearchQuery) => {
  const allOptionsOfType = query.filter(isIsAllowedForStrictTransformationsOption);
  const trueOptionFound = allOptionsOfType.some((option) => option.isAllowedForStrictTransformations);
  const falseOptionFound = allOptionsOfType.some((option) => !option.isAllowedForStrictTransformations);

  if (trueOptionFound === falseOptionFound) {
    return null;
  }
  return allOptionsOfType[0].isAllowedForStrictTransformations;
};

export const useGetSearchQueryValue = () => {
  const { formatMessage } = useIntl();
  return useCallback(
    (query: SearchOption) => {
      if (isCategoryOption(query)) {
        return query.category;
      }
      if (isComplexityOption(query)) {
        return query.complexity;
      }
      if (isPlaintextOption(query)) {
        return query.text;
      }
      if (isIsInPresetsOption(query)) {
        return formatMessage(messages[query.isInPresets ? 'isInPresets' : 'notInPresets']);
      }
      if (isIsAllowedForStrictTransformationsOption(query)) {
        return formatMessage(messages[query.isAllowedForStrictTransformations ? 'allowedInStrictTransformations' : 'notAllowedInStrictTransformations']);
      }
      return undefined;
    },
    [formatMessage]
  );
};
